import {
  createAction,
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction,
} from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import {
  capitalize,
  EventStatus,
  EventTypes,
  ReadStatus,
  ReducerStates,
  RESOLVED_EVENT_WINDOW,
  RESIDENT_FIRST_NAME_LENGTH,
  sortAlphabetical,
  TabTypes,
  UnitTypes,
} from "../../helpers/constants";
import {
  dashboardServices,
  EventScope,
  IChangeRecord,
  IEvent,
  IEventNotes,
  IEventsAsOf,
  IResident,
  IRFIDTag,
  ITileCounts,
  IUnit,
  IUnitDashboard,
  IWatchedFlagData,
  ACCESS_REFUSED,
  WARNING_EVENTS_LIMIT,
} from "../../services/dashboard.services";
import { IUserDetails } from "../../services/header.services";
import {
  assignEventResponder,
  postNewComment,
  resolveEventWithPassword,
  resolveEventWithPin,
  toggleUnitPaused,
} from "../app/asyncThunkActions";
import {
  createUnit,
  deleteCommunityUnit,
  deleteResident,
} from "../settings/settingsThunks";
import {
  changeSelectedCommunity,
  changeSelectedOrganization,
  setAllNotifications,
} from "../header/headerSlice";
import { IUnitActivityData } from "./eventDataTypes";
import { logout } from "../login/loginSlice";
import { addResident, updateResident } from "../settings/settingsThunks";
import {
  loadAllUnitUserData,
  loadOrganizationData,
} from "../header/headerThunks";
import { StorageKeys } from "../../services/constants";
import { getPastTimestampUTC } from "../../helpers/datetime";
import { changeFeedEnabled } from "../../helpers/firebase";
import { getEventsToUpdateFirebase } from "../../helpers/utils";

export interface IEnhancedUnit extends IUnit {
  notVisible?: boolean;
}
export interface IResolveEventDataPin {
  email: string;
  pin: string;
  eventID: string;
  resolutionID: string;
  eventNote?: string;
}
export interface IResolveEventDataPassword {
  email: string;
  password: string;
  eventID: string;
  resolutionID: string;
  eventNote?: string;
}

export const isHardEvent = (eventEl: IEvent) =>
  eventEl.notification_type === "hard";
export const isSoftEvent = (eventEl: IEvent) =>
  eventEl.notification_type === "soft";
export const isActiveUser = <
  T extends Pick<IUserDetails, "first_name" | "last_name">
>(
  user: T
) => user.first_name !== null && user.last_name !== null;

export const invitationEmailFailed = <
  T extends Pick<IUserDetails, "email_status">
>(
  user: T
) =>
  user.email_status !== null &&
  (user.email_status === "invalid_email" ||
    user.email_status === "unclassified");

const getFirstName = (name: string | null, fullName: boolean | undefined) => {
  if (name === null) return "";
  // For short names, ignore full name property
  if (name.length <= RESIDENT_FIRST_NAME_LENGTH) return name;
  if (fullName) return name;
  //Get the first part of the name and check if its longer than allowed length
  const nameFirstPart = name.split(" ")[0];
  return nameFirstPart.length > RESIDENT_FIRST_NAME_LENGTH
    ? `${nameFirstPart.slice(0, RESIDENT_FIRST_NAME_LENGTH)}.`
    : nameFirstPart;
};

export type ResidentNameOptions = {
  fullFirstName?: boolean;
  fullLastName?: boolean;
};
// Get a unit resident's name based on an array of IResidents
export const getResidentName = (
  residents: IResident[] | null | undefined,
  options?: ResidentNameOptions
) => {
  if (residents !== undefined && residents !== null) {
    if (residents.length > 0) {
      const resident = residents[0];
      let firstName = getFirstName(resident.first_name, options?.fullFirstName);

      let lastName =
        resident.last_name !== null
          ? options?.fullLastName
            ? capitalize(resident.last_name)
            : resident.last_name.slice(0, 1).toUpperCase()
          : "";

      let fullName = "";
      if (firstName !== "") {
        fullName = capitalize(firstName);
      }
      if (lastName !== "") {
        if (fullName !== "") {
          fullName = fullName.concat(" ", lastName);
        } else {
          fullName = lastName;
        }
      }
      return fullName;
    } else {
      return "";
    }
  } else {
    return "";
  }
};

// Get a unit resident's name based on an array of IResidents
export const getResidentCity = (residents?: IResident[] | null) => {
  if (residents !== undefined && residents !== null && residents.length > 0) {
    const resident = residents[0];
    let city = resident.city ?? "";
    if (city !== "") {
      city = capitalize(city);
    }
    return city;
  } else {
    return "";
  }
};

export const isHelpAtHome = (unit?: IEnhancedUnit) => {
  if (unit === undefined) return;
  return unit.unit_type === UnitTypes.HELP_AT_HOME;
};

export const sortHelpAtHome = <T extends { residents: IResident[] | null }>(
  a: T,
  b: T
) => {
  if (a.residents === null || a.residents.length === 0) return 1;
  if (b.residents === null || b.residents.length === 0) return -1;

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  const residentNameA = getResidentName(a.residents, {
    fullFirstName: false,
  });
  const residentCityA = getResidentCity(a.residents);

  const residentNameB = getResidentName(b.residents, {
    fullFirstName: false,
  });
  const residentCityB = getResidentCity(b.residents);

  if (collator.compare(residentCityA, residentCityB) !== 0) {
    return collator.compare(residentCityA, residentCityB);
  }
  if (collator.compare(residentNameA, residentNameB) !== 0) {
    return collator.compare(residentNameA, residentNameB);
  }
  // names must be equal
  return 0;
};

export const sortSeniorCommunity = <T extends { name: string }>(a: T, b: T) => {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  return collator.compare(a.name, b.name);
};

/**
 * Sorts the provided units array 
 * @param {EnhancedUnit[] | IUnit[]} units units array
 * @returns {EnhancedUnit[] | IUnit[]} A filtered unit array
 * 
 * Sorts in the following way
 * - If all units are help at home, first by resident.city & resident.first name & resident.last_name 
 * - If mixed units (help at home, common and resident), sort first by unit_type:resident & unit.number followed by unit_type:common & unit.number, sort 
    next by unit_type:help_at_home & resident.city & resident.first_name & resident.last_name
 * - If help at home unit has no resident or is not installed, sort at the end
 * 
 */
export const sortUnits = (units?: IEnhancedUnit[] | IUnit[]) => {
  if (units === undefined) return [];

  const unitCopy: typeof units = JSON.parse(JSON.stringify(units));

  // All units are help at home
  const allHelpAtHome = units.every((unit) => isHelpAtHome(unit));
  const allResident = units.every(
    (unit) => unit.unit_type === UnitTypes.RESIDENT
  );

  // Order units depending on how the unit types are mixed
  if (allHelpAtHome) {
    return unitCopy.sort((a, b) => sortHelpAtHome(a, b));
  } else if (allResident) {
    return unitCopy.sort((a, b) => sortSeniorCommunity(a, b));
  } else {
    const residentUnits = [...unitCopy]
      .filter((unit) => unit.unit_type === UnitTypes.RESIDENT)
      .sort((a, b) => sortSeniorCommunity(a, b));

    const otherUnits = [...unitCopy]
      .filter(
        (unit) => unit.unit_type !== UnitTypes.RESIDENT && !isHelpAtHome(unit)
      )
      .sort((a, b) => sortSeniorCommunity(a, b));

    const helpAtHomeUnits = [...unitCopy]
      .filter((unit) => isHelpAtHome(unit))
      .sort((a, b) => sortHelpAtHome(a, b));

    return [...residentUnits, ...otherUnits, ...helpAtHomeUnits];
  }
};

export interface IDashboardState {
  units?: IEnhancedUnit[];
  orgUnits: { [key: string]: IUnit[] };
  allUnits: { [key: string]: IUnit };
  communityEvents?: IEvent[];
  state: ReducerStates;
  firebaseState: ReducerStates;
  openedEventsTab: string[];
  unitDashboard: { [key: string]: IUnitDashboard | undefined };
  unitEvents: { [key: string]: IEvent[] | undefined };
  unitActivity: { [key: string]: IUnitActivityData | undefined };
  updateNotifications: boolean;
  startMonitoringNewEvents: boolean;
  addedEvent: boolean;
  errorCode: string;
  errorMessage?: string;
  openTray: boolean;
  minimizedTray: boolean;
  selectedTab?: number;
  tabs?: { id: string; type: TabTypes }[] | undefined;
  eventData: { [key: string]: { apiFailed: boolean } };
  communityFilter: string[];
  statusFilter: string[];
  userFilter: string[];
  isCommunityFiltered: boolean;
  isStatusFiltered: boolean;
  isUserFiltered: boolean;
  resolvedEvents?: IEvent[];
  rfidTags: Record<string, IRFIDTag[]>;
  changeFeed: IChangeFeedState;
}

// off runs the Firebase listeners, loading waits for the API, on follows the change feed
export type ChangeFeedMode = "off" | "loading" | "on";

export interface IChangeFeedState {
  mode: ChangeFeedMode;
  // The community or organization the feed was loaded for
  scope?: string;
  // Units on screen in an organization, a community uses its units
  unitIDs?: string[];
  criticalAsOf?: string;
  // All notifications only, its resolved events
  resolvedAsOf?: string;
  warningAsOf?: string;
  // The last warning load hit the limit, so more unread warnings exist past it
  warningsFull: boolean;
  refillingWarnings: boolean;
  // The last refill failed, so the next one waits longer
  refillFailed: boolean;
  tileCounts: Record<string, ITileCounts>;
  eventVersions: Record<string, string>;
}

export const initialChangeFeedState: IChangeFeedState = {
  mode: changeFeedEnabled ? "loading" : "off",
  warningsFull: false,
  refillingWarnings: false,
  refillFailed: false,
  tileCounts: {},
  eventVersions: {},
};

export const initialLDashboardState: IDashboardState = {
  units: undefined,
  orgUnits: {},
  allUnits: {},
  communityEvents: undefined,
  state: ReducerStates.IDLE,
  firebaseState: ReducerStates.IDLE,
  openedEventsTab: [],
  unitDashboard: {},
  unitEvents: {},
  unitActivity: {},
  updateNotifications: false,
  startMonitoringNewEvents: false,
  addedEvent: false,
  errorCode: "",
  errorMessage: "",
  openTray: false,
  minimizedTray: false,
  selectedTab: undefined,
  tabs: undefined,
  eventData: {},
  communityFilter: [],
  statusFilter: [],
  userFilter: [],
  isCommunityFiltered: false,
  isStatusFiltered: false,
  isUserFiltered: false,
  resolvedEvents: undefined,
  rfidTags: {},
  changeFeed: initialChangeFeedState,
};

// Gets the community units and events for the community
export const getCommunityData = createAsyncThunk(
  "dashboard/getCommunityData",
  async (community_id: string) => {
    const units = await dashboardServices.getUnits(community_id);
    return units;
  }
);

export interface ILoadActiveEventsArgs {
  scope: string;
  eventScope: EventScope;
  unitIDs?: string[];
  // Only the warning list is loaded again, the rest of the starting state stays
  warningsOnly?: boolean;
  // Loads the tile counts again too
  communityID?: string;
  // All notifications shows critical events and the ones resolved recently, no warnings
  allNotifications?: boolean;
}

type ActiveList = "warning" | "critical" | "resolved";

// One starting list, shown as soon as it loads instead of waiting for the others
export const activeEventsLoaded = createAction<{
  scope: string;
  list: ActiveList;
  events: IEventsAsOf;
}>("dashboard/activeEventsLoaded");

// Loads the starting lists for the change feed
export const loadActiveEvents = createAsyncThunk(
  "dashboard/loadActiveEvents",
  async (args: ILoadActiveEventsArgs, { dispatch }) => {
    const show = (list: ActiveList) => (events: IEventsAsOf) => {
      dispatch(activeEventsLoaded({ scope: args.scope, list, events }));
      return events;
    };
    const [warning, critical, resolved, units] = await Promise.all([
      args.allNotifications
        ? undefined
        : dashboardServices
            .getActiveEvents(args.eventScope, false)
            .then(show("warning")),
      args.warningsOnly
        ? undefined
        : dashboardServices
            .getActiveEvents(args.eventScope, true)
            .then(show("critical")),
      args.allNotifications
        ? dashboardServices
            .getResolvedEvents(
              args.eventScope,
              getPastTimestampUTC(RESOLVED_EVENT_WINDOW)
            )
            .then(show("resolved"))
        : undefined,
      args.communityID ? dashboardServices.getUnits(args.communityID) : [],
    ]);
    return { warning, critical, resolved, units };
  }
);

// A resolved record carries no event, so a view showing who resolved it reads it once
export const refreshResolvedEvent = createAsyncThunk(
  "dashboard/refreshResolvedEvent",
  async (eventID: string) => dashboardServices.getEvent(eventID, false),
  {
    // Only all notifications and the notifications table show the responder of a resolved event
    condition: (eventID, { getState }) => {
      const state = getState() as {
        headerState: { allNotifications?: boolean };
        notificationsState: { notifications?: IEvent[] };
      };
      return (
        !!state.headerState.allNotifications ||
        !!state.notificationsState.notifications?.some(
          (event) => event.id === eventID
        )
      );
    },
  }
);

// Gets the community units and events for the community
export const getUnitStatus = createAsyncThunk(
  "dashboard/getUnitStatus",
  async (unit_id: string) => {
    const timeNow = DateTime.now().toUTC();
    const endTime = timeNow.toISO();
    const startTime = timeNow.minus({ hours: 12 }).toISO();

    const [dashboard, events, activity] = await Promise.all([
      dashboardServices.getUnitDashboard(unit_id),
      dashboardServices.getUnitEvents(unit_id),
      dashboardServices.getUnitActivityData(unit_id, startTime, endTime),
    ]);
    return { dashboard, events, activity };
  }
);

// Marks all warnings as read for the community
export const markAllWarningsRead = createAsyncThunk(
  "dashboard/markAllWarningsRead",
  async (community_id: string) => {
    const response = await dashboardServices.markAllWarningsRead(community_id);
    return response;
  }
);

// Toggles an unit watch flag
export const toggleUnitWatchFlag = createAsyncThunk(
  "dashboard/toggleUnitWatchFlag",
  async (watchFlagData: IWatchedFlagData) => {
    const response = await dashboardServices.toggleWatchedFlag(watchFlagData);
    return response;
  }
);

// Get an event by id
export const getEventDataDashboard = createAsyncThunk(
  "dashboard/getEventDataDashboard",
  async (eventID: string) => {
    const response = await dashboardServices.getEvent(eventID);
    return response;
  }
);

type DashboardDraft = Draft<IDashboardState>;

// Adds new events and updates the ones already listed
const mergeCommunityEvents = (state: DashboardDraft, events: IEvent[]) => {
  if (state.communityEvents === undefined) {
    state.communityEvents = events;
  } else {
    const eventIDs = state.communityEvents.map((item) => item.id);
    const eventsToUpdate = events.filter((incomingEvent) =>
      eventIDs.includes(incomingEvent.id)
    );

    const indexesEvents: [number, number][] = [];
    state.communityEvents.forEach((localEvent, index) => {
      const foreignIndex = eventsToUpdate.findIndex(
        (newEvent) => newEvent.id === localEvent.id
      );
      if (foreignIndex !== -1) {
        indexesEvents.push([index, foreignIndex]);
      }
    });

    indexesEvents.forEach((indexTuple) => {
      if (state.communityEvents === undefined) return;

      const oldEvent = state.communityEvents[indexTuple[0]];
      const newEvent = eventsToUpdate[indexTuple[1]];

      oldEvent.responder_id = newEvent.responder_id;
      oldEvent.status = newEvent.status;

      oldEvent.time_assigned = newEvent.time_assigned ?? oldEvent.time_assigned;
      oldEvent.time_resolved = newEvent.time_resolved ?? oldEvent.time_resolved;
      oldEvent.event_resolution_id =
        newEvent.event_resolution_id ?? oldEvent.event_resolution_id;
      oldEvent.read_status = newEvent.read_status ?? oldEvent.read_status;

      // Check if event has been opened in tab, if it has, don't replace data
      if (!state.openedEventsTab.includes(newEvent.id)) {
        oldEvent.data = newEvent.data;
      }
    });
    const eventsToAdd = events.filter(
      (incomingEvent) => !eventIDs.includes(incomingEvent.id)
    );
    // If there are new events and we already started monitoring, open floating tray
    if (eventsToAdd.length > 0) {
      // Checks to see if only a single pause_notification event is being added
      const isPauseEvent =
        eventsToAdd.length === 1 &&
        eventsToAdd[0].event_type === EventTypes.pause_notification;
      state.communityEvents = state.communityEvents.concat(eventsToAdd);
      // If it's just a pause_notification_getting added, addedEvent will not
      // be set to true
      if (state.startMonitoringNewEvents && !isPauseEvent)
        state.addedEvent = true;
    }
  }
  state.communityEvents.sort((a, b) =>
    sortAlphabetical(b.time_created, a.time_created)
  );
  state.firebaseState = ReducerStates.SUCCEEDED;
};

// Marks a critical event resolved or a warning read, unless its tab is open
const deactivateCommunityEvent = (state: DashboardDraft, eventID: string) => {
  if (state.communityEvents === undefined) return;
  const isOpened = state.tabs?.find((tab) => tab.id === eventID);
  // If tab is not open, then delete event
  if (isOpened === undefined) {
    const localEventIndex = state.communityEvents.findIndex(
      (event) => event.id === eventID
    );
    if (localEventIndex !== -1) {
      if (isHardEvent(state.communityEvents[localEventIndex])) {
        state.communityEvents[localEventIndex].status = EventStatus.RESOLVED;
      } else {
        state.communityEvents[localEventIndex].read_status = ReadStatus.READ;
      }
    }
  }
};

const isActiveCritical = (event: IEvent) =>
  isHardEvent(event) && event.status !== EventStatus.RESOLVED;
export const isUnreadWarning = (event: IEvent) =>
  isSoftEvent(event) && event.read_status === ReadStatus.UNREAD;

// Critical events can have no notification type, the panel expects hard
const asCriticalEvent = (event: IEvent): IEvent => ({
  ...event,
  notification_type: event.notification_type ?? "hard",
});

// The version an event's list state is known at, the list's as-of unless a newer record changed it
const heldEventVersion = (
  feed: IChangeFeedState,
  eventID: string,
  listAsOf: string
) => {
  const version = feed.eventVersions[eventID];
  return version !== undefined && version > listAsOf ? version : listAsOf;
};

// Keeps the newest counts for each unit
const mergeTileCounts = (state: DashboardDraft, units: IUnit[]) => {
  units.forEach((unit) => {
    const held = state.changeFeed.tileCounts[unit.id];
    if (unit.tile_counts && (!held || unit.tile_counts.version > held.version))
      state.changeFeed.tileCounts[unit.id] = unit.tile_counts;
  });
};

// Replaces a starting list, keeping events a newer record already changed
const replaceActiveEvents = (
  state: DashboardDraft,
  list: IEventsAsOf,
  critical: boolean
) => {
  const feed = state.changeFeed;
  const asOf = list.asOf ?? "";
  const isNewer = (eventID: string) =>
    (feed.eventVersions[eventID] ?? "") > asOf;
  const listedIDs = new Set(list.events.map((event) => event.id));
  const isActive = critical ? isActiveCritical : isUnreadWarning;

  state.communityEvents
    ?.filter(
      (event) =>
        isActive(event) && !listedIDs.has(event.id) && !isNewer(event.id)
    )
    .forEach((event) => deactivateCommunityEvent(state, event.id));

  const events = list.events
    .filter((event) => !isNewer(event.id))
    .map((event) =>
      critical
        ? asCriticalEvent(event)
        : { ...event, read_status: ReadStatus.UNREAD }
    );
  mergeCommunityEvents(state, events);

  if (critical) {
    feed.criticalAsOf = asOf;
  } else {
    feed.warningAsOf = asOf;
    feed.warningsFull = list.events.length >= WARNING_EVENTS_LIMIT;
  }
};

// Applies change records, versions make any order or repeat give the same result
const applyRecords = (state: DashboardDraft, records: IChangeRecord[]) => {
  const feed = state.changeFeed;
  if (feed.mode !== "on") return;
  const unitIDs = new Set(
    feed.unitIDs ?? state.units?.map((unit) => unit.id) ?? []
  );

  records.forEach((record) => {
    if (!unitIDs.has(record.unit_id)) return;
    const counts = record.unit_counts;
    const version = counts.version;
    const heldCounts = feed.tileCounts[record.unit_id];
    if (!heldCounts || version > heldCounts.version)
      feed.tileCounts[record.unit_id] = counts;

    const critical = record.kind.startsWith("unresolved");
    // Without a warning list, as on all notifications, only critical events are followed
    if (!critical && feed.warningAsOf === undefined) return;
    const listAsOf = (critical ? feed.criticalAsOf : feed.warningAsOf) ?? "";

    // A bulk change carries no event, so the counts say when the unit's list is empty
    if (record.kind === "counts_changed") {
      if (counts.unread !== 0) return;
      state.communityEvents
        ?.filter(
          (event) =>
            event.unit_id === record.unit_id &&
            isUnreadWarning(event) &&
            version > heldEventVersion(feed, event.id, listAsOf)
        )
        .forEach((event) => {
          feed.eventVersions[event.id] = version;
          deactivateCommunityEvent(state, event.id);
        });
      return;
    }

    const eventID = record.event_id;
    if (!eventID || version <= heldEventVersion(feed, eventID, listAsOf))
      return;
    feed.eventVersions[eventID] = version;

    if (record.event && record.event_key) {
      const [event] = getEventsToUpdateFirebase(
        { [record.event_key]: record.event },
        record.kind === "unread_added"
      );
      mergeCommunityEvents(state, [critical ? asCriticalEvent(event) : event]);
    }
    if (
      record.kind === "unread_removed" ||
      record.kind === "unresolved_removed"
    )
      deactivateCommunityEvent(state, eventID);
  });
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialLDashboardState,
  reducers: {
    clearUnitStatus(state, action) {
      delete state.unitDashboard[action.payload];
      delete state.unitEvents[action.payload];
      delete state.eventData[action.payload];
    },
    clearAllUnitStatus(state) {
      state.unitDashboard = {};
      state.unitEvents = {};
      state.eventData = {};
    },
    addTab(state, action) {
      const { id, type } = action.payload;
      if (state.tabs !== undefined) {
        const existingTabIndex = state.tabs.findIndex((tab) => tab.id === id);
        // If trying to add a tab that already exists, switch focus to it
        if (existingTabIndex === -1) {
          state.tabs = [...state.tabs, { id, type }];
          state.selectedTab = state.tabs.length - 1;
        } else {
          state.selectedTab = existingTabIndex;
          state.openTray = true;
          state.minimizedTray = false;
          return;
        }
      } else {
        state.tabs = [{ id, type }];
        state.selectedTab = 0;
      }
      state.openTray = true;
      state.minimizedTray = false;
    },
    closeTab(state, action) {
      const index = action.payload;
      let newSelectedTab = state.selectedTab;
      if (state.tabs !== undefined && newSelectedTab !== undefined) {
        let newTabs = [...state.tabs];
        const isCurrentlySelected = index === state.selectedTab;
        const isLast = index === newTabs.length - 1;
        // Check if tab to close is selected
        if (isCurrentlySelected) {
          newTabs.splice(index, 1);
          // If the new lenght of tabs is 0, then close tab tray
          if (newTabs.length > 0) {
            if (isLast) {
              newSelectedTab = newTabs.length - 1;
            } else {
              newSelectedTab = index;
            }
          } else {
            newSelectedTab = undefined;
            state.openTray = false;
          }
        } else {
          // If tab to delete is to the left of the selected tab, selectedTab index is reduced by one
          newTabs.splice(index, 1);
          if (index < newSelectedTab) {
            newSelectedTab = newSelectedTab - 1;
          }
        }
        state.selectedTab = newSelectedTab;
        state.tabs = newTabs;
      }
    },
    changeFocus(state, action) {
      const index = action.payload;
      state.selectedTab = index;
      state.openTray = true;
      state.minimizedTray = false;
    },
    minimizeTabTray(state) {
      state.openTray = false;
      state.minimizedTray = true;
    },
    maximizeTabTray(state) {
      state.openTray = true;
      state.minimizedTray = false;
    },
    closeTabTray(state) {
      state.openTray = false;
      state.minimizedTray = false;
      state.selectedTab = undefined;
      state.tabs = undefined;
    },
    setCommunityEvents(state, action) {
      mergeCommunityEvents(state, action.payload);
    },
    setResolvedEvents(state, action) {
      const events: IEvent[] = action.payload;
      state.resolvedEvents = events;
      state.resolvedEvents.sort((a, b) =>
        sortAlphabetical(b.time_created, a.time_created)
      );
      state.firebaseState = ReducerStates.SUCCEEDED;
    },
    startLoadingDashboard(state) {
      state.firebaseState = ReducerStates.PENDING;
    },
    failedFirebaseDashboard(state) {
      state.firebaseState = ReducerStates.FAILED;
    },
    updateEventDetails(state, action: PayloadAction<IEvent>) {
      const incomingEvent: IEvent = action.payload;
      if (state.communityEvents === undefined) return;
      const eventIndex = state.communityEvents.findIndex((event) => {
        return event.id === incomingEvent.id;
      });
      if (eventIndex !== -1) {
        state.communityEvents[eventIndex].status = incomingEvent.status;
        state.communityEvents[eventIndex].responder_id =
          incomingEvent.responder_id;

        if (state.communityEvents[eventIndex].time_assigned !== undefined)
          state.communityEvents[eventIndex].time_assigned =
            incomingEvent.time_assigned;
        if (state.communityEvents[eventIndex].time_resolved !== undefined)
          state.communityEvents[eventIndex].time_resolved =
            incomingEvent.time_resolved;
        if (state.communityEvents[eventIndex].event_resolution_id !== undefined)
          state.communityEvents[eventIndex].event_resolution_id =
            incomingEvent.event_resolution_id;
      }
    },
    updateEventNotes(
      state,
      action: PayloadAction<{ eventID: string; eventNotes: IEventNotes[] }>
    ) {
      const eventID = action.payload.eventID;
      const eventNotes: IEventNotes[] = action.payload.eventNotes;
      if (state.communityEvents === undefined) return;
      const eventIndex = state.communityEvents.findIndex(
        (event) => event.id === eventID
      );
      // If event exists
      if (eventIndex !== -1) {
        // If event doesn't have notes, replace them directly
        // Notes should be sorted in decending order by timestamp
        const notes = state.communityEvents[
          eventIndex
        ].event_notes?.sort((a, b) =>
          sortAlphabetical(b.timestamp, a.timestamp)
        );
        if (notes === undefined) {
          state.communityEvents[eventIndex].event_notes = eventNotes;
        } else {
          // Search for new notes and add them at the beggining
          eventNotes.forEach((note) => {
            const noteIndex = notes.findIndex(
              (localNote) => localNote.id === note.id
            );
            if (noteIndex === -1) {
              notes.unshift(note);
            }
          });
        }
      }
    },
    // All notifications shows resolved events only for a while after they were created,
    // including ones resolved while on screen
    dropResolvedEventsBefore(state, action: PayloadAction<string>) {
      const startWindow = action.payload;
      state.resolvedEvents = state.resolvedEvents?.filter(
        (event) => event.time_created > startWindow
      );
      state.communityEvents = state.communityEvents?.filter(
        (event) =>
          !isHardEvent(event) ||
          event.status !== EventStatus.RESOLVED ||
          event.time_created > startWindow ||
          state.tabs?.some((tab) => tab.id === event.id)
      );
    },
    removeCommunityEvent(state, action: PayloadAction<string>) {
      deactivateCommunityEvent(state, action.payload);
    },
    applyChangeRecords(state, action: PayloadAction<IChangeRecord[]>) {
      applyRecords(state, action.payload);
    },
    // The feed failed or the page slept, so the starting state is loaded again
    reloadChangeFeed(state) {
      if (state.changeFeed.mode === "on") state.changeFeed.mode = "loading";
    },
    // Users who never load the feed, like installers, keep the listeners
    turnOffChangeFeed(state) {
      state.changeFeed = { ...initialChangeFeedState, mode: "off" };
    },
    updateUnit(state, action: PayloadAction<IUnit>) {
      if (state.units) {
        const unit = action.payload;
        const unitIndex = state.units.findIndex(
          (localUnit) => localUnit.id === unit.id
        );
        if (unitIndex !== -1) {
          state.units[unitIndex] = { ...state.units[unitIndex], ...unit };
        }
        state.units = sortUnits(state.units);
      } else {
        const unit = action.payload;
        if (unit && unit.community_id) {
          const unitIndex = state.orgUnits[unit.community_id].findIndex(
            (localUnit) => localUnit.id === unit.id
          );
          if (unitIndex !== -1) {
            state.orgUnits[unit.community_id][unitIndex] = {
              ...state.orgUnits[unit.community_id][unitIndex],
              ...unit,
            };
          }
        }
      }
    },
    startMonitoringNewEvents(state) {
      state.startMonitoringNewEvents = true;
    },
    consumeAddedEvent(state) {
      state.addedEvent = false;
    },
    setCommunityFilter(state, action) {
      state.communityFilter = action.payload;
      localStorage.setItem(StorageKeys.COMMUNITY_FILTER, action.payload);
    },
    setIsCommunityFiltered(state, action) {
      state.isCommunityFiltered = action.payload;
      localStorage.setItem(StorageKeys.IS_COMMUNITY_FILTERED, action.payload);
    },
    setIsStatusFiltered(state, action) {
      state.isStatusFiltered = action.payload;
      localStorage.setItem(StorageKeys.IS_STATUS_FILTERED, action.payload);
    },
    setIsUserFiltered(state, action) {
      state.isUserFiltered = action.payload;
      localStorage.setItem(StorageKeys.IS_USER_FILTERED, action.payload);
    },
    setStatusFilter(state, action) {
      state.statusFilter = action.payload;
      localStorage.setItem(StorageKeys.STATUS_FILTER, action.payload);
    },
    setUserFilter(state, action) {
      state.userFilter = action.payload;
      localStorage.setItem(StorageKeys.USER_FILTER, action.payload);
    },
    setRFIDTags(state, action) {
      state.rfidTags = action.payload;
      console.log(state.rfidTags);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCommunityData.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(getCommunityData.fulfilled, (state, action) => {
        const units: IEnhancedUnit[] = action.payload;
        let tempUnits: IEnhancedUnit[] = [];
        // sort communities by unit number
        if (units && units.length > 0) {
          tempUnits = sortUnits(units);
        }
        state.units = tempUnits;
        // Records keep the counts current only while the feed is on
        if (state.changeFeed.mode !== "off" && units)
          mergeTileCounts(state, units);
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(loadActiveEvents.pending, (state, action) => {
        const { scope, unitIDs, warningsOnly } = action.meta.arg;
        if (warningsOnly) {
          state.changeFeed.refillingWarnings = true;
          return;
        }
        state.firebaseState = ReducerStates.PENDING;
        state.changeFeed.scope = scope;
        state.changeFeed.unitIDs = unitIDs;
      })
      .addCase(loadActiveEvents.fulfilled, (state, action) => {
        const feed = state.changeFeed;
        if (action.meta.arg.warningsOnly) {
          feed.refillingWarnings = false;
          feed.refillFailed = false;
        }
        // A response for a community or organization that is no longer on screen
        if (action.meta.arg.scope !== feed.scope) return;

        const { warning, critical, units } = action.payload;
        // Without the header the backend has the change feed off, so the listeners take over
        if (warning?.asOf === null || critical?.asOf === null) {
          state.changeFeed = { ...initialChangeFeedState, mode: "off" };
          return;
        }
        // A refill only replaces the warning list, a full load in progress turns the feed on
        if (action.meta.arg.warningsOnly) return;
        mergeTileCounts(state, units);
        feed.mode = "on";
        state.startMonitoringNewEvents = true;
      })
      .addCase(activeEventsLoaded, (state, action) => {
        const { scope, list, events } = action.payload;
        // A list for a view no longer on screen, or from a backend with the change feed off
        if (scope !== state.changeFeed.scope || events.asOf === null) return;
        if (list === "resolved") {
          state.changeFeed.resolvedAsOf = events.asOf;
          state.resolvedEvents = events.events
            .map(asCriticalEvent)
            .sort((a, b) => sortAlphabetical(b.time_created, a.time_created));
        } else {
          replaceActiveEvents(state, events, list === "critical");
        }
      })
      .addCase(refreshResolvedEvent.fulfilled, (state, action) => {
        const event = asCriticalEvent(action.payload);
        if (state.communityEvents?.some((item) => item.id === event.id)) {
          mergeCommunityEvents(state, [event]);
        } else if (
          state.changeFeed.scope === "all" &&
          !state.resolvedEvents?.some((item) => item.id === event.id)
        ) {
          // Resolved while all notifications loaded, so in neither starting list.
          // The cleanup drops it later if it was created before the window
          state.resolvedEvents = [
            event,
            ...(state.resolvedEvents ?? []),
          ].sort((a, b) => sortAlphabetical(b.time_created, a.time_created));
        }
      })
      .addCase(loadActiveEvents.rejected, (state, action) => {
        if (action.meta.arg.warningsOnly) {
          state.changeFeed.refillingWarnings = false;
          state.changeFeed.refillFailed = true;
          return;
        }
        if (action.meta.arg.scope !== state.changeFeed.scope) return;
        // A user with mixed access across the organization's communities is refused
        // the combined call, so the organization goes back to the listeners.
        // Any other failed load stays loading and is tried again
        if (
          "unit_id" in action.meta.arg.eventScope &&
          action.error.code === ACCESS_REFUSED
        ) {
          state.changeFeed = { ...initialChangeFeedState, mode: "off" };
          return;
        }
        state.firebaseState = ReducerStates.FAILED;
      })
      .addCase(getCommunityData.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(markAllWarningsRead.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(markAllWarningsRead.fulfilled, (state) => {
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(markAllWarningsRead.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(getUnitStatus.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(getUnitStatus.fulfilled, (state, action) => {
        state.state = ReducerStates.SUCCEEDED;
        if (action.meta.arg) {
          const payload = action.payload.dashboard;
          payload.local_time_updated = DateTime.now().toISO();
          state.unitDashboard[action.meta.arg] = payload;
          state.unitEvents[action.meta.arg] = action.payload.events;
          state.unitActivity[action.meta.arg] = action.payload.activity;
        }
      })
      .addCase(getUnitStatus.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(postNewComment.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(postNewComment.fulfilled, (state) => {
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(postNewComment.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(changeSelectedCommunity, (state) => {
        state.state = ReducerStates.IDLE;
        state.firebaseState = ReducerStates.IDLE;
        state.openTray = false;
        state.selectedTab = initialLDashboardState.selectedTab;
        state.tabs = initialLDashboardState.tabs;
        state.communityEvents = initialLDashboardState.communityEvents;
        state.resolvedEvents = initialLDashboardState.resolvedEvents;
        state.units = initialLDashboardState.units;
        state.allUnits = initialLDashboardState.allUnits;
        state.openedEventsTab = initialLDashboardState.openedEventsTab;
        state.unitDashboard = initialLDashboardState.unitDashboard;
        state.unitEvents = initialLDashboardState.unitEvents;
        state.startMonitoringNewEvents =
          initialLDashboardState.startMonitoringNewEvents;
        state.addedEvent = initialLDashboardState.addedEvent;
        state.eventData = initialLDashboardState.eventData;
        state.changeFeed = initialChangeFeedState;
      })
      .addCase(changeSelectedOrganization, (state) => {
        state.state = ReducerStates.IDLE;
        state.firebaseState = ReducerStates.IDLE;
        state.openTray = false;
        state.selectedTab = initialLDashboardState.selectedTab;
        state.tabs = initialLDashboardState.tabs;
        state.communityEvents = initialLDashboardState.communityEvents;
        state.resolvedEvents = initialLDashboardState.resolvedEvents;
        state.units = initialLDashboardState.units;
        state.allUnits = initialLDashboardState.allUnits;
        state.openedEventsTab = initialLDashboardState.openedEventsTab;
        state.unitDashboard = initialLDashboardState.unitDashboard;
        state.unitEvents = initialLDashboardState.unitEvents;
        state.startMonitoringNewEvents =
          initialLDashboardState.startMonitoringNewEvents;
        state.addedEvent = initialLDashboardState.addedEvent;
        state.eventData = initialLDashboardState.eventData;
        state.changeFeed = initialChangeFeedState;
      })
      .addCase(setAllNotifications, (state) => {
        state.state = ReducerStates.IDLE;
        state.firebaseState = ReducerStates.IDLE;
        state.openTray = false;
        state.selectedTab = initialLDashboardState.selectedTab;
        state.tabs = initialLDashboardState.tabs;
        state.communityEvents = initialLDashboardState.communityEvents;
        state.resolvedEvents = initialLDashboardState.resolvedEvents;
        state.units = initialLDashboardState.units;
        state.openedEventsTab = initialLDashboardState.openedEventsTab;
        state.unitDashboard = initialLDashboardState.unitDashboard;
        state.unitEvents = initialLDashboardState.unitEvents;
        state.startMonitoringNewEvents =
          initialLDashboardState.startMonitoringNewEvents;
        state.addedEvent = initialLDashboardState.addedEvent;
        state.eventData = initialLDashboardState.eventData;
        state.changeFeed = initialChangeFeedState;
      })
      .addCase(assignEventResponder.pending, (state, action) => {
        const fromDashboard = action.meta.arg.fromDashboard;

        state.updateNotifications = fromDashboard;
        if (fromDashboard) state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(assignEventResponder.fulfilled, (state, action) => {
        const newEvent: IEvent = action.payload;
        const eventIndex = state.communityEvents?.findIndex(
          (event) => event.id === newEvent.id
        );
        if (
          state.communityEvents !== undefined &&
          eventIndex !== -1 &&
          eventIndex !== undefined
        ) {
          state.communityEvents[eventIndex] = newEvent;
        }

        if (state.updateNotifications) state.state = ReducerStates.SUCCEEDED;
        state.updateNotifications = initialLDashboardState.updateNotifications;
      })
      .addCase(assignEventResponder.rejected, (state, action) => {
        if (state.updateNotifications) {
          state.state = ReducerStates.FAILED;
          state.errorMessage = action.error.message;
          state.updateNotifications =
            initialLDashboardState.updateNotifications;
        }
      })
      .addCase(resolveEventWithPin.pending, (state, action) => {
        const fromDashboard = action.meta.arg.fromDashboard;
        state.updateNotifications = fromDashboard;
        if (fromDashboard) state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(resolveEventWithPin.fulfilled, (state, action) => {
        const newEvent: IEvent = action.payload;
        const communityEvents = state.communityEvents;
        if (communityEvents) {
          const eventIndex = communityEvents.findIndex(
            (eventEl) => eventEl.id === newEvent.id
          );
          if (eventIndex >= 0) {
            communityEvents[eventIndex] = newEvent;
          } else {
            communityEvents.push(newEvent);
          }
        }
        if (state.updateNotifications) state.state = ReducerStates.SUCCEEDED;
        state.updateNotifications = initialLDashboardState.updateNotifications;
      })
      .addCase(resolveEventWithPin.rejected, (state, action) => {
        if (state.updateNotifications) {
          state.state = ReducerStates.FAILED;
          state.errorMessage = action.error.message;
          state.updateNotifications =
            initialLDashboardState.updateNotifications;
        }
      })
      .addCase(toggleUnitPaused.pending, (state, action) => {
        const fromDashboard = action.meta.arg.fromDashboard;
        if (fromDashboard) {
          state.updateNotifications = fromDashboard;
          state.state = ReducerStates.PENDING;
          state.errorCode = initialLDashboardState.errorCode;
          state.errorMessage = initialLDashboardState.errorMessage;
        }
      })
      .addCase(toggleUnitPaused.fulfilled, (state, action) => {
        if (action.payload) {
          const { unit, event } = action.payload;
          const unitIndex = state.units?.findIndex(
            (current) => current.id === unit.id
          );
          if (
            state.units !== undefined &&
            unitIndex !== -1 &&
            unitIndex !== undefined
          ) {
            state.units[unitIndex].is_notifications_paused =
              unit.is_notifications_paused;
            state.units[unitIndex].pause_notifications_expiration =
              unit.pause_notifications_expiration;
            state.units[unitIndex].security_notification_enabled =
              unit.security_notification_enabled;
          }
          const eventIndex = state.communityEvents?.findIndex(
            (current) => current.id === event.id
          );
          if (
            state.communityEvents !== undefined &&
            eventIndex !== -1 &&
            eventIndex !== undefined
          ) {
            state.communityEvents[eventIndex] = event;
          }
        }

        if (state.updateNotifications) state.state = ReducerStates.SUCCEEDED;

        state.updateNotifications = initialLDashboardState.updateNotifications;
      })
      .addCase(toggleUnitPaused.rejected, (state, action) => {
        if (state.updateNotifications) {
          state.state = ReducerStates.FAILED;
          state.errorMessage = action.error.message;
          state.updateNotifications =
            initialLDashboardState.updateNotifications;
        }
      })
      .addCase(resolveEventWithPassword.pending, (state, action) => {
        const fromDashboard = action.meta.arg.fromDashboard;
        state.updateNotifications = fromDashboard;
        if (fromDashboard) state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(resolveEventWithPassword.fulfilled, (state, action) => {
        const newEvent: IEvent = action.payload;
        const communityEvents = state.communityEvents;
        if (communityEvents) {
          const eventIndex = communityEvents.findIndex(
            (eventEl) => eventEl.id === newEvent.id
          );
          if (eventIndex >= 0) {
            communityEvents[eventIndex] = newEvent;
          } else {
            communityEvents.push(newEvent);
          }
        }
        if (state.updateNotifications) state.state = ReducerStates.SUCCEEDED;
        state.updateNotifications = initialLDashboardState.updateNotifications;
      })
      .addCase(resolveEventWithPassword.rejected, (state, action) => {
        if (state.updateNotifications) {
          state.state = ReducerStates.FAILED;
          state.errorMessage = action.error.message;
          state.updateNotifications =
            initialLDashboardState.updateNotifications;
        }
      })
      .addCase(toggleUnitWatchFlag.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(toggleUnitWatchFlag.fulfilled, (state, action) => {
        const newUnit: IUnit = action.payload;
        const unitIndex = state.units?.findIndex(
          (unit) => unit.id === newUnit.id
        );
        if (
          state.units !== undefined &&
          unitIndex !== -1 &&
          unitIndex !== undefined
        ) {
          state.units[unitIndex].is_watched = newUnit.is_watched;
        }
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(toggleUnitWatchFlag.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(getEventDataDashboard.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(getEventDataDashboard.fulfilled, (state, action) => {
        const event: IEvent = action.payload;
        if (state.communityEvents !== undefined) {
          const localEventIndex = state.communityEvents.findIndex(
            (_event) => _event.id === event.id
          );
          if (localEventIndex !== -1) {
            state.communityEvents[localEventIndex] = {
              ...state.communityEvents[localEventIndex],
              ...event,
            };
            state.openedEventsTab.push(event.id);
          } else {
            state.openedEventsTab.push(event.id);
            state.communityEvents.push(event);
          }
          if (state.eventData[event.id] !== undefined) {
            state.eventData[event.id].apiFailed = false;
          } else {
            state.eventData[event.id] = { apiFailed: false };
          }
        } else {
          state.communityEvents = [event];
        }
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(getEventDataDashboard.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
        if (state.eventData[action.meta.arg] !== undefined) {
          state.eventData[action.meta.arg].apiFailed = true;
        } else {
          state.eventData[action.meta.arg] = { apiFailed: true };
        }
      })
      .addCase(updateResident.fulfilled, (state, action) => {
        const unit = state.units?.find(
          (unit) => unit.id === action.payload.unit_id
        );
        if (unit) {
          if (unit.residents && unit.residents.length > 0) {
            unit.residents[0] = { ...unit.residents[0], ...action.payload };
          } else {
            unit.residents = [action.payload];
          }
        }
      })
      .addCase(addResident.fulfilled, (state, action) => {
        if (state.units && action.payload) {
          const unitToUpdate = state.units.find(
            (unit) => unit.id === action.payload.unit_id
          );
          if (unitToUpdate) {
            unitToUpdate.residents = [action.payload];
          }
        }
      })
      .addCase(loadOrganizationData.fulfilled, (state, action) => {
        state.orgUnits = action.payload.orgUnits;
      })
      .addCase(loadAllUnitUserData.fulfilled, (state, action) => {
        state.allUnits = action.payload.allUnits;
        state.state = ReducerStates.SUCCEEDED;
      })
      .addCase(loadAllUnitUserData.pending, (state) => {
        state.state = ReducerStates.PENDING;
        state.errorCode = initialLDashboardState.errorCode;
        state.errorMessage = initialLDashboardState.errorMessage;
      })
      .addCase(loadAllUnitUserData.rejected, (state, action) => {
        state.state = ReducerStates.FAILED;
        state.errorMessage = action.error.message;
      })
      .addCase(logout.type, (state) => {
        return initialLDashboardState;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        if (action.payload && state.units) {
          const newUnit = action.payload.newUnit;
          if (
            action.meta.arg.unit_type === UnitTypes.HELP_AT_HOME &&
            action.payload.newResident
          ) {
            newUnit.residents = [action.payload.newResident];
          }
          const newUnits: IUnit[] = [...state.units, action.payload.newUnit];
          state.units = sortUnits(newUnits);
        }
      })
      .addCase(deleteCommunityUnit.fulfilled, (state, action) => {
        if (action.payload) {
          const deletedUnitIndex = state.units?.findIndex(
            (unit) => unit.id === action.meta.arg
          );
          if (
            deletedUnitIndex !== undefined &&
            deletedUnitIndex !== -1 &&
            state.units
          ) {
            state.units.splice(deletedUnitIndex, 1);
            state.units = sortUnits(state.units);
          }
        }
      })
      .addCase(deleteResident.fulfilled, (state, action) => {
        if (state.units) {
          const residentID = action.meta.arg.resident_id;
          state.units.forEach((unit) => {
            if (unit.residents) {
              unit.residents = unit.residents.filter(
                (resident) => resident.id !== residentID
              );
            }
          });
        }
      });
  },
});

const { reducer } = dashboardSlice;
export const {
  clearUnitStatus,
  clearAllUnitStatus,
  addTab,
  closeTab,
  changeFocus,
  minimizeTabTray,
  maximizeTabTray,
  closeTabTray,
  setCommunityEvents,
  startLoadingDashboard,
  updateEventDetails,
  updateEventNotes,
  removeCommunityEvent,
  applyChangeRecords,
  reloadChangeFeed,
  turnOffChangeFeed,
  dropResolvedEventsBefore,
  updateUnit,
  failedFirebaseDashboard,
  startMonitoringNewEvents,
  consumeAddedEvent,
  setCommunityFilter,
  setStatusFilter,
  setUserFilter,
  setIsCommunityFiltered,
  setIsStatusFiltered,
  setIsUserFiltered,
  setResolvedEvents,
  setRFIDTags,
} = dashboardSlice.actions;

export default reducer;
